import { Button, Eyebrow, Icon, ImagePlate, Reveal, Rule, Section, Stat, Tag } from "@/ds";
import type { Project } from "@/content/projects";
import { projects } from "@/content/projects";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { AxenPopout } from "./AxenPopout";
import { ProjectArticle } from "./ProjectArticle";

/**
 * Case-study layout, following the design system's own `ui_kits/portfolio/Project.jsx`:
 * plate and headline, then an asymmetric 1.6fr/1fr body with the spec table in
 * the narrow right rail, a two-up figure grid, and a rule before the next link.
 */
export function ProjectPage({ project }: { project: Project }) {
  const position = projects.findIndex((p) => p.slug === project.slug) + 1;
  const total = projects.length;

  return (
    <div className="bg-surface-page text-text-primary font-body min-h-screen">
      <Header />

      <main>
        <Section width="wide" className="pb-7">
          <div className="flex justify-between items-baseline gap-5 mb-6">
            <Eyebrow index={project.index}>Case study</Eyebrow>
            <span className="font-label text-label uppercase text-text-faint">
              Experience {String(position).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>

          <h1 className="font-display font-regular text-display-2 max-w-[26ch] mb-6">{project.title}</h1>

          <Reveal variant="fade">
            <ImagePlate
              src={project.hero.src}
              alt={project.hero.alt}
              ratio={project.hero.ratio}
              index={project.hero.index}
              caption={project.hero.caption}
            />
          </Reveal>
        </Section>

        <Section width="wide" flush className="pb-section-y">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-7 md:gap-9 items-start">
            <div className="flex flex-col gap-5 max-w-measure">
              <p className="text-body-lg text-text-primary">{project.lead}</p>

              {/* list-disc is explicit: Tailwind's preflight strips markers from ul. */}
              <ul className="m-0 pl-[1.1em] list-disc marker:text-text-faint flex flex-col gap-3 text-body text-text-body">
                {project.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-x-7 gap-y-5 pt-4">
                {project.stats.map((stat) => (
                  <Stat key={stat.label} value={stat.value} label={stat.label} size="md" />
                ))}
              </div>
            </div>

            <aside className="border-t border-border-ink pt-4">
              <table className="w-full border-collapse">
                <tbody>
                  {project.spec.map((row) => (
                    <tr key={row.label} className="border-b border-border-hairline">
                      <th className="text-left py-[10px] font-label text-label uppercase text-text-muted font-regular w-[38%] align-top">
                        {row.label}
                      </th>
                      <td className="py-[10px] text-body-sm text-text-primary">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex flex-wrap gap-2 mt-5">
                {project.tags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </div>
            </aside>
          </div>

          <ProjectArticle slug={project.slug} sections={project.sections} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-9">
            {project.figures.map((figure, i) => (
              <Reveal key={figure.index} variant="fade" index={i}>
                <ImagePlate
                  src={figure.src}
                  alt={figure.alt}
                  ratio={figure.ratio}
                  index={figure.index}
                  caption={figure.caption}
                />
              </Reveal>
            ))}
          </div>

          <Rule className="mt-9" />

          <div className="flex flex-wrap justify-between items-center gap-5 mt-5">
            <span className="font-label text-label uppercase text-text-muted">Next — {project.next.label}</span>
            <div className="flex items-center gap-6">
              <Button variant="ghost" href={project.next.href} icon={<Icon name="arrow-right" size={16} />}>
                Next case study
              </Button>
              <Button variant="ghost" href="/#work" icon={<Icon name="arrow-right" size={16} />}>
                Back to experience
              </Button>
            </div>
          </div>
        </Section>
      </main>

      <Footer />
      <AxenPopout />
    </div>
  );
}
